import dayjs from 'dayjs';
import { DateType, MeasurementType, StateTimeDisplay, StateTimeType, groups } from '../utils';
import { DeviceDataProps, IndicatorProps } from '.';
import { DeviceMonitorValue } from './components/DeviceCompare';
import { keepPercentOrLine, keepTwoOrLine } from '@/utils/utils';

type Groups = Record<string, number[]>;

const baseSeriesOption = (seriesInfo: DeviceDataProps, vote: any) => {
  if (!vote) return;
  const isTypeChart = vote?.measurementType === MeasurementType.INSTANT || vote.type === 0 ? 'line' : 'bar';
  const seriesData =
    vote.type === StateTimeType.USERATIO
      ? seriesInfo?.chartData?.map((i: any) => keepPercentOrLine(i.value)) || []
      : seriesInfo?.chartData?.map((i: any) => keepTwoOrLine(i.value)) || [];
  return {
    data: seriesData,
    type: isTypeChart,
    smooth: true,
    symbol: 'circle',
    showSymbol: false,
    name: `${seriesInfo?.inventoryName}${seriesInfo?.indicatorName ? '-' + seriesInfo.indicatorName : ''}`,
    symbolSize: 10,
  };
};

export const getBasicChartOptionByHis = (hisChartData: IndicatorProps[], timeResolution: DateType) => {
  const merged = mergeIndicators(hisChartData, groups);
  return {
    grid: {
      left: 50,
      right: 50,
      top: 50,
      containLabel: true,
    },
    backgroundColor: 'transparent',
    legend: {
      type: 'scroll',
      right: 10,
      itemGap: 16,
      data: hisChartData.map(s => {
        const isBar = s?.measurementType === MeasurementType.INSTANT || s.type === 0;
        return {
          name: s.indicatorName ?? StateTimeDisplay[s.type as StateTimeType],
          icon: isBar
            ? 'path://M0 4 L12 4 L12 8 L0 8 Z' // 扁条 (12x12)
            : 'path://M0 0 L12 0 L12 12 L0 12 Z', // 方块 (12x4)
        };
      }),
    },
    dataZoom: [
      {
        type: 'slider',
        bottom: 15,
        height: 20,
        labelFormatter: (value: string) => {
          const time = hisChartData?.[0]?.chartData?.[Number(value)]?.time;
          return dayjs(time).format(xLabel(timeResolution));
        },
      },
      { type: 'inside' },
    ],
    // color: ['#28a745', '#ffc107', '#007bff'],
    color: ['#52E7FF', '#FFCB47', '#2D8DFF', '#57FB8B', '#FF4D4F', '#CE90D1'],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      padding: [8, 12, 8, 12],
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 12,
        lineHeight: 20,
      },
      formatter: (tooltipData: any) => {
        return `
         <div> ${dayjs(Number(tooltipData[0]?.axisValue)).format(
           timeResolution === DateType.DAY ? 'YYYY-MM-DD HH:mm' : xLabel(timeResolution)
         )}</div>
          <div>${tooltipData
            ?.map(
              (item: any, index: number) =>
                `<div style='width:230px;display: flex;align-items: center;margin-bottom: 4px;'><span style='display:inline-block;width:8px;height:8px;background:${
                  item?.color
                };border-radius:50%;margin-right:6px'></span> <div style='max-width: 140px;;overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;'>${item.seriesName}</div><span style='flex:1;text-align: right'>
          ${item.value}
             ${hisChartData[index].unitName!}</span></div></div>`
            )
            .join('')}</div>
        `;
      },
    },
    xAxis: [
      {
        type: 'category',
        axisPointer: {
          type: 'line',
        },
        axisTick: {
          show: false,
        },
        data: hisChartData?.[0]?.chartData?.map((i: any) => i.time),
        axisLabel: {
          formatter: (value: number) => {
            return dayjs(Number(value)).format(xLabel(timeResolution));
          },
          margin: 8,
          lineHeight: 14,
        },
      },
    ],
    yAxis: getSafeYAxis(hisChartData),
    series:
      hisChartData?.map((item, index) => {
        const isTypeChart = item?.measurementType === MeasurementType.INSTANT || item.type === 0 ? 'line' : 'bar';
        const groupIndex = merged.findIndex(m => {
          if (Array.isArray(m.indicatorId)) {
            return m.indicatorId.includes(item.indicatorId);
          }
          return m.indicatorId === item.indicatorId;
        });

        // const colors = ['#28a745', '#ffc107', '#007bff'];
        // const isPhaseVoltage = [10004, 10005, 10006].includes(item?.indicatorId);
        // const colorIndex = [10004, 10005, 10006].indexOf(item?.indicatorId);
        // const color = isPhaseVoltage ? colors[colorIndex] : undefined;

        return {
          type: isTypeChart,
          smooth: true,
          symbol: 'circle',
          showSymbol: false,
          symbolSize: 10,
          name: item.indicatorName ?? StateTimeDisplay[item.type as StateTimeType],
          yAxisIndex: groupIndex >= 0 ? groupIndex : 0,
          // color,
          data:
            item.type === StateTimeType.USERATIO
              ? item.chartData?.map((a, index) => keepPercentOrLine(a.value))
              : item.chartData?.map((a, index) => keepTwoOrLine(a.value)),
        };
      }) || [],
  };
};

export const getBasicChartOptionByObj = (
  objChartData: DeviceDataProps[],
  timeResolution: DateType,
  deviceMonitor?: DeviceMonitorValue
) => {
  return {
    grid: {
      left: 50,
      right: 10,
      containLabel: true,
    },
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      padding: [8, 12, 8, 12],
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 12,
        lineHeight: 20,
      },
      formatter: (tooltipData: any) => {
        return `
          <div> ${dayjs(Number(tooltipData[0]?.axisValue)).format(
            timeResolution === DateType.DAY ? 'YYYY-MM-DD HH:mm' : xLabel(timeResolution)
          )}</div>
          <div>${tooltipData
            ?.map(
              (item: any) =>
                `<div style='width:260px;display: flex;align-items: center;margin-bottom: 4px;'><span style='display:inline-block;width:8px;height:8px;background:${
                  item?.color
                };border-radius:50%;margin-right:6px'></span> <div style='max-width: 200px;;overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;'>${item.seriesName}</div><span style='flex:1;text-align: right'>${
                  item.value ?? '--'
                }${objChartData[0].unitName!}</span></div></div>`
            )
            .join('')}</div>
        `;
      },
    },
    legend: {
      type: 'scroll',
      right: 10,
      itemGap: 16,
      data: objChartData.map(s => {
        const isBar = deviceMonitor?.measurementType === MeasurementType.INSTANT || deviceMonitor?.type === 0;
        return {
          name: s.inventoryName! + (s.indicatorName ? '-' + s.indicatorName : ''),
          icon: isBar
            ? 'path://M0 4 L12 4 L12 8 L0 8 Z' // 扁条 (12x12)
            : 'path://M0 0 L12 0 L12 12 L0 12 Z', // 方块 (12x4)
        };
      }),
    },
    dataZoom: [
      {
        type: 'slider',
        bottom: 15,
        height: 20,
        labelFormatter: (value: string) => {
          const time = objChartData?.[0]?.chartData?.[Number(value)]?.time;
          return dayjs(time).format(xLabel(timeResolution));
        },
      },
      { type: 'inside' },
    ],
    color: ['#52E7FF', '#FFCB47', '#2D8DFF', '#57FB8B', '#FF4D4F', '#CE90D1'],
    xAxis: [
      {
        type: 'category',
        axisPointer: {
          type: 'line',
        },
        axisTick: {
          // alignWithLabel: true,
          show: false,
        },
        data: objChartData?.[0]?.chartData?.map(i => i.time),
        axisLabel: {
          formatter: (value: number) => {
            return dayjs(Number(value)).format(xLabel(timeResolution));
          },
          margin: 8,
          lineHeight: 14,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: `${deviceMonitor?.label ?? StateTimeDisplay[deviceMonitor?.type as StateTimeType]}${
          objChartData[0].unitName
        }`,
        nameGap: 8,
        splitLine: {
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
        nameTextStyle: {
          color: 'rgba(255,255,255,0.85)',
          lineHeight: 20,
        },
        axisLine: {},
        axisLabel: {
          margin: 8,
          color: 'rgba(255,255,255,0.85)',
          lineHeight: 20,
        },
      },
    ],
    series: objChartData?.map((item: any) => baseSeriesOption(item, deviceMonitor)) || [],
  };
};

const xLabel = (v: string) => {
  if (v === DateType.MONTH || v === DateType.WEEK) {
    return 'YYYY-MM-DD';
  } else if (v === DateType.YEAR) {
    return 'YYYY-MM';
  } else {
    return 'HH:mm';
  }
};

const getSafeYAxisA = (IndicatorEchart: IndicatorProps[]) => {
  if (IndicatorEchart && IndicatorEchart.length > 0) {
    return IndicatorEchart.map((item: IndicatorProps, index: number) => {
      return {
        type: 'value',
        name: `${item?.indicatorName || StateTimeDisplay[item.type as StateTimeType]}${item?.unitName || ''}`,
        position: index === 0 ? 'left' : 'right',
        offset: index > 1 ? (index - 1) * 60 : 0,
        axisLabel: { formatter: '{value}' },
        alignTicks: true,
      };
    });
  }
  return [
    {
      type: 'value',
      show: false,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false },
    },
  ];
};

export function mergeIndicators(raw: IndicatorProps[], groups: Groups): any[] {
  const used = new Set<number>(); // 标记已处理过的 id
  const result: any[] = [];

  for (const [groupName, ids] of Object.entries(groups)) {
    const matched = raw.filter(item => ids.includes(item.indicatorId!));

    if (matched.length > 1) {
      // 合并多条
      result.push({
        ...matched[0], // 继承第一条的其他属性
        indicatorName: groupName,
        indicatorId: matched.map(m => m.indicatorId),
      });
      matched.forEach(m => used.add(m.indicatorId!));
    }
  }

  raw.forEach(item => {
    if (!used.has(item.indicatorId!)) {
      result.push(item);
    }
  });

  return result;
}

const getSafeYAxis = (IndicatorEchart: IndicatorProps[]) => {
  if (IndicatorEchart && IndicatorEchart.length > 0) {
    const merged = mergeIndicators(IndicatorEchart, groups);
    return merged.map((item: IndicatorProps, index: number) => {
      return {
        type: 'value',
        name: `${item?.indicatorName || StateTimeDisplay[item.type as StateTimeType]}${item?.unitName || ''}`,
        position: index === 0 ? 'left' : 'right',
        offset: index > 1 ? (index - 1) * 60 : 0,
        axisLabel: { formatter: '{value}' },
        alignTicks: true,
      };
    });
  }
  return [
    {
      type: 'value',
      show: false,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false },
    },
  ];
};
